"use client";
import {
  SearchDropdownType,
  SearchFieldDropdown,
  SearchFieldElement,
  SearchFieldFocusEvent,
} from "@/app/layouts/standard/header/search-bar/search-bar-dropdown/searchFieldDropdown";
import { MAX_SEARCH_SUGGESTIONS } from "@/app/layouts/standard/header/search-bar/search-bar-dropdown/searchSuggestionsDropdown";
import { SearchPageType } from "@/app/search/baseSearchPage";
import { getSearchSuggestions, SearchSuggestionsResponse } from "@/fetch/algolia/getSearchSuggestions";
import { useSearchHistory } from "@/app/hooks/useSearchHistory";
import { useAppDispatch } from "@/store/hooks";
import { setSearchOverlayDarkened } from "@/store/overlayDarkeningSlice";
import { setSearchDropdownVisible } from "@/store/searchDropdownVisibleSlice";
import { AppState } from "@/store/store";
import { dispatchAnalyticsEvent } from "@/utils/analyticsEventSender";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import CloseIcon from "public/next-public/common/icons/close-icon.svg";
import SearchIcon from "public/next-public/common/icons/search-icon.svg";
import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import styles from "./algoliaSearchField.module.scss";

interface SearchFieldDropdownProps {
  isCategoryPage?: boolean;
  searchPageType?: SearchPageType;
  isHomePage?: boolean;
}

const AlgoliaSearchField = ({ isCategoryPage, searchPageType, isHomePage = false }: SearchFieldDropdownProps) => {
  const dispatch = useAppDispatch();
  const menuVisible = useSelector((state: AppState) => state.drawerMenuOpenSlice.openMenu !== "none");
  const searchContainerRef = useRef<HTMLFormElement>(null);
  const searchInputGroupContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [rawSearchFieldValue, setRawSearchFieldValue] = useState<string | undefined>();
  const [debouncedSearchFieldValue, setDebouncedSearchFieldValue] = useState<string | undefined>();
  const [searchSuggestionsResponse, setSearchSuggestionsResponse] = useState<
    SearchSuggestionsResponse | undefined | null
  >();
  const [showSuggestionsTrigger, setShowSuggestionsTrigger] = useState<number>(0);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { searchHistory, recordSearch, forgetSearch } = useSearchHistory();
  const [searchFieldFocusEvent, setSearchFieldFocusEvent] = useState<SearchFieldFocusEvent | null>(null);

  // Detect the search param on page load and put the search term into the search bar
  useEffect(() => {
    const currentQueryParamValue = searchParams?.get("query");
    if (searchInputRef.current) {
      searchInputRef.current.value = currentQueryParamValue ?? "";
    }
    // Keep React state in sync with the URL so that:
    // - Re-focusing on a search results page can trigger suggestions (query present)
    // - Navigating away clears stale query state so a blank field never pops query suggestions
    setRawSearchFieldValue(currentQueryParamValue ?? undefined);
    if (!currentQueryParamValue) {
      setDebouncedSearchFieldValue(undefined);
    }
  }, [searchParams]);

  const debouncedInput = useDebouncedCallback((searchInputText: string) => {
    setDebouncedSearchFieldValue(searchInputText);
  }, 300);

  useEffect(() => {
    const fetchAlgoliaSearchSuggestions = async () => {
      try {
        const result = await getSearchSuggestions(debouncedSearchFieldValue, MAX_SEARCH_SUGGESTIONS);
        setSearchSuggestionsResponse(result);
      } catch (error) {
        // Error intentionally ignored
      }
    };
    if (debouncedSearchFieldValue && debouncedSearchFieldValue.length > 0) {
      fetchAlgoliaSearchSuggestions();
    }
  }, [debouncedSearchFieldValue]);

  const handleSearchFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRawSearchFieldValue(event.target.value);
    debouncedInput(event.target.value);
  };

  const setSearchQueryUrlParam = useCallback(
    async (searchValue: string | null | undefined) => {
      const params = new URLSearchParams("");
      if (searchValue) {
        params.set("query", searchValue);
      }

      try {
        await dispatchAnalyticsEvent({
          eventName: "site_search",
          search: {
            query: searchValue,
          },
        });
      } catch (error) {
        console.error("Analytics failed, but continuing with router push:", error);
      }

      router.push(`/search?${params.toString()}`);
    },
    [router],
  );

  const onSearchSuggestionClick = useCallback(
    (query: string) => {
      setRawSearchFieldValue(query);
      setSearchQueryUrlParam(query);
    },
    [setSearchQueryUrlParam],
  );

  const onSearchInputSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
    setSearchQueryUrlParam(searchInputRef?.current?.value);
    recordSearch(searchInputRef?.current?.value);
  };

  const isMobileOrTabletSize = (): boolean => {
    return window.innerWidth < 768;
  };

  const scrollToSearchBar = (): void => {
    const elementPosition = searchInputRef.current?.getBoundingClientRect().top;
    if (elementPosition == null) return;

    // Only the homepage reaches here. Below md its open dropdown slides the sticky
    // header away, so the field takes the top of the viewport; above md it does
    // not, so the field keeps clear of the header instead.
    const headerOffset = isMobileOrTabletSize() ? HOMEPAGE_SEARCH_TOP_GAP : HEADER_SEARCH_OFFSET;

    const offsetPosition = elementPosition + window.scrollY - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const isTouchDevice = (): boolean => {
    return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  };

  const handleInputFocus = (): void => {
    // If the input has a value that hasn't been synced to debouncedSearchFieldValue
    // (e.g. after navigating to a search results page), sync it now so that
    // suggestions are fetched and the overlay can darken correctly.
    const currentInputValue = searchInputRef.current?.value;
    if (currentInputValue && currentInputValue !== debouncedSearchFieldValue) {
      setDebouncedSearchFieldValue(currentInputValue);
    }
    setShowSuggestionsTrigger(showSuggestionsTrigger + 1);
    // Only the homepage renders the search field inline in the page. In the header it is already
    // pinned by the sticky container, and asking to scroll it into view reads as a downward scroll.
    if (isHomePage && isTouchDevice()) {
      scrollToSearchBar();
    }
  };

  const dropdownItemChooseCallback = useCallback(
    (elementDetails: SearchFieldElement): void => {
      if (
        elementDetails.dropdownType === SearchDropdownType.LOCAL_HISTORY ||
        elementDetails.dropdownType === SearchDropdownType.SEARCH_SUGGESTIONS
      ) {
        recordSearch(elementDetails.label);
        onSearchSuggestionClick(elementDetails.label);
      }
    },
    [recordSearch, onSearchSuggestionClick],
  );

  const dropdownItemDismissCallback = useCallback(
    (elementDetails: SearchFieldElement): void => {
      if (elementDetails.dropdownType === SearchDropdownType.LOCAL_HISTORY) {
        forgetSearch(elementDetails.label);
      }
    },
    [forgetSearch],
  );

  // Get search dropdown visibility from global state
  const searchDropdownVisible = useSelector((state: AppState) => state.searchDropdownVisibleSlice.isVisible);

  // Update darkening when either menu or search dropdown visibility changes
  useEffect(() => {
    dispatch(setSearchOverlayDarkened(menuVisible || searchDropdownVisible));
  }, [menuVisible, searchDropdownVisible, dispatch]);

  // Handle search dropdown visibility changes - dispatch to global state
  const handleSearchDropdownVisibilityChange = useCallback(
    (isVisible: boolean) => {
      dispatch(setSearchDropdownVisible(isVisible));
    },
    [dispatch],
  );

  return (
    <div id={styles.search} className={isHomePage ? styles.homePageSearch : ""}>
      <form
        id="search-bar"
        className={`${styles.searchContainer} ${isHomePage ? styles.homePageSearchContainer : ""}`}
        ref={searchContainerRef}
        onSubmit={onSearchInputSubmit}
      >
        <div className={styles.searchGroupContainer} ref={searchInputGroupContainerRef}>
          <input
            className={`${styles.searchInput} ${searchDropdownVisible ? styles.dropdownVisible : ""}`}
            placeholder=" "
            value={rawSearchFieldValue}
            onChange={handleSearchFieldChange}
            ref={searchInputRef}
            onFocus={handleInputFocus}
          />
          <button
            type="reset"
            className={styles["clearButton"]}
            onClick={(event) => {
              setRawSearchFieldValue("");
              setDebouncedSearchFieldValue("");
              event.stopPropagation();
              searchInputRef.current?.focus();
            }}
          >
            <CloseIcon width="16px" height="16px" className={styles["clearButtonSVG"]} fill="currentColor" />
          </button>
          <span className={styles.searchInputOverlay}></span>
        </div>
        <SearchFieldDropdown
          searchHistory={searchHistory}
          inputTarget={searchInputRef}
          containerTarget={searchInputGroupContainerRef}
          searchBarTarget={searchContainerRef}
          searchFieldFocusEvent={searchFieldFocusEvent}
          query={debouncedSearchFieldValue}
          searchSuggestionsResponse={searchSuggestionsResponse}
          showTrigger={showSuggestionsTrigger}
          dropdownItemChooseCallback={dropdownItemChooseCallback}
          dropdownItemDismissCallback={dropdownItemDismissCallback}
          onVisibilityChange={handleSearchDropdownVisibilityChange}
        />

        <div className={styles["input-group-append"]}>
          <button
            type={"submit"}
            id={styles["search-button"]}
            className={`btn ${searchDropdownVisible ? styles.dropdownVisible : ""}`}
          >
            <SearchIcon className={styles["searchButtonSVG"]} stroke="currentColor" height={20} width={20} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default AlgoliaSearchField;

/** Gap left above the homepage search field once the header has slid away. */
const HOMEPAGE_SEARCH_TOP_GAP = 16;

/** Clearance for the sticky header when the field scrolls up underneath it. */
const HEADER_SEARCH_OFFSET = 62.5;
